import type { Product } from '../data/products';
import { GLOBAL_SETTINGS } from '../data/settings';

export function calcSqft(widthMm: string | number, heightMm: string | number): number {
  const wIn = Math.ceil(
    (typeof widthMm === 'string' ? parseFloat(widthMm) : widthMm) /
      GLOBAL_SETTINGS.mmToInchDivisor || 0,
  );
  const hIn = Math.ceil(
    (typeof heightMm === 'string' ? parseFloat(heightMm) : heightMm) /
      GLOBAL_SETTINGS.mmToInchDivisor || 0,
  );
  return Math.ceil((wIn * hIn) / GLOBAL_SETTINGS.sqFtDivisor);
}

export function calcItemPrice(
  product: Product | null,
  qty: number,
  widthMm: string | number,
  heightMm: string | number,
): number {
  if (!product) return 0;

  const method = product.pricing_method;
  const rate = product.base_rate;
  const minPrice = product.min_price;

  if (method === 'Piece' || method === 'Trip' || method === 'Panel' || method === 'Set') {
    // Flat per-unit methods: a fixed rate per unit; dimensions do not affect
    // price. (The legacy oversize-surcharge paths were removed 2026-06-30 — the
    // 2026 catalog prices oversize via the Area sqft floor, not a flat-rate
    // surcharge; no product sets oversize_applies.)
    return qty * rate;
  }

  if (method === 'Area') {
    const sqft = Math.max(calcSqft(widthMm, heightMm), product.min_area ?? 0);
    const unitPrice = sqft * rate;
    const min = minPrice || 0;
    const price = Math.max(unitPrice, min);
    return qty * price;
  }

  return 0;
}

export interface AddonItemBase {
  product: Product;
  qty: number;
  widthMm: string;
  heightMm: string;
  inheritDims: boolean;
  /** POA add-ons only: rep-entered flat amount (string as typed). When the
   *  add-on's product.pricing_method === 'POA', the line uses this flat value
   *  instead of rate math. Empty / non-numeric → 0. */
  poaPrice?: string;
}

export interface LineItemBase {
  product: Product | null;
  qty: number;
  widthMm: string;
  heightMm: string;
  addons?: AddonItemBase[];
  /** 'adhoc' marks a free-text, non-catalog line. Catalog lines omit this
   *  field (or set 'catalog'). Adhoc lines price as adhocUnitPrice x qty. */
  source?: 'catalog' | 'adhoc';
  /** Adhoc sell price per unit — a string, exactly as typed in the form. */
  adhocUnitPrice?: string;
  /** Manual per-line price override — a string, exactly as typed in the form.
   *  Adhoc lines: when valid (>= 0) it replaces the entire line total, ignoring qty.
   *  Catalog lines: it acts as a FLOOR — the line is max(override, catalog total),
   *  so an override can raise/hold the line but never discount below the catalog
   *  price (line-level discounting is disallowed; discounts go via the grand total).
   *  Empty / unset / non-numeric / negative fall through to the normal math. */
  priceOverride?: string;
}

/** Effective billable sqft for an Area line: the larger of the computed sqft
 *  and the product's minimum-area floor. Used so inheriting $/sqft add-ons
 *  bill on the same floored figure as the base. */
export function computeEffectiveSqft(
  product: Product,
  widthMm: string | number,
  heightMm: string | number,
): number {
  return Math.max(calcSqft(widthMm, heightMm), product.min_area ?? 0);
}

export function calcItemTotal(item: LineItemBase): number {
  // Parse the manual per-line override once. Empty / unset / non-numeric /
  // negative → null (falls through to normal math).
  const overrideRaw = item.priceOverride;
  const override = overrideRaw !== undefined && overrideRaw !== '' ? parseFloat(overrideRaw) : NaN;
  const validOverride = !Number.isNaN(override) && override >= 0 ? override : null;

  // Adhoc (non-catalog) lines: the override wins as-is (no catalog reference to
  // floor against), else a plain unit price × qty. This path stays clear of the
  // regression-locked catalog formulas.
  if (item.source === 'adhoc') {
    if (validOverride !== null) return validOverride;
    const unitPrice = parseFloat(item.adhocUnitPrice ?? '') || 0;
    return Math.max(0, unitPrice) * Math.max(0, item.qty || 0);
  }

  if (!item.product) return 0;

  const mainPrice = calcItemPrice(item.product, item.qty, item.widthMm, item.heightMm);
  const baseEffSqft =
    item.product.pricing_method === 'Area'
      ? computeEffectiveSqft(item.product, item.widthMm, item.heightMm)
      : null;
  const addonPrice = (item.addons || []).reduce((sum, a) => {
    if (a.product.pricing_method === 'POA') {
      const p = parseFloat(a.poaPrice ?? '');
      return sum + (Number.isNaN(p) || p < 0 ? 0 : p);
    }
    if (a.product.pricing_method === 'Area' && a.inheritDims && baseEffSqft != null) {
      return sum + Math.max(0, a.qty) * (baseEffSqft * a.product.base_rate);
    }
    const w = a.inheritDims ? item.widthMm : a.widthMm;
    const h = a.inheritDims ? item.heightMm : a.heightMm;
    return sum + calcItemPrice(a.product, a.qty, w, h);
  }, 0);
  const catalogTotal = mainPrice + addonPrice;

  // Catalog override FLOOR: the override may raise or hold the line total but
  // never price it below the catalog-computed total (no line-level discounts;
  // discounting is a whole-quote grand-total concern). Intentional 2026-07-01
  // change — the 42 catalog regression tests set no override, so they are
  // unaffected; the override block's own tests pin this floor.
  return validOverride !== null ? Math.max(validOverride, catalogTotal) : catalogTotal;
}
